// FD-612 — which browser is this console running in, for the sign-in helper card?
//
// The AirBridge sign-in helper ships for Microsoft Edge and Google Chrome only
// (FD-ADR-047 §4). The card names the browser and shows its logo, so the answer
// must be one we can stand behind: anything we cannot positively identify as Edge
// or Chrome is `unsupported`, never "probably Chrome".
//
// `navigator.userAgentData.brands` is preferred where it exists (Chromium only).
// When it is present and non-empty it is authoritative: a Chromium browser that is
// neither Edge nor Chrome (Brave, Opera, Arc…) lists its own brand and is NOT the
// browser the store listing serves, even though its user-agent string says Chrome.
// The UA string is the fallback for browsers that have no `userAgentData`.
//
// Pure and injectable: the caller passes the navigator-shaped object, so the tests
// do not need to patch the global.

export type SupportedBrowser = 'edge' | 'chrome';

export type DetectedBrowser =
  | { kind: SupportedBrowser; name: string }
  | { kind: 'unsupported'; name: string | null };

export interface NavigatorLike {
  userAgent?: string;
  userAgentData?: { brands?: ReadonlyArray<{ brand: string; version?: string }> } | null;
}

const EDGE: DetectedBrowser = { kind: 'edge', name: 'Edge' };
const CHROME: DetectedBrowser = { kind: 'chrome', name: 'Chrome' };

export function detectBrowser(nav: NavigatorLike | undefined = globalThis.navigator): DetectedBrowser {
  const brands = nav?.userAgentData?.brands;
  if (Array.isArray(brands) && brands.length > 0) {
    const names = brands.map((b) => b.brand);
    if (names.includes('Microsoft Edge')) return EDGE;
    if (names.includes('Google Chrome')) return CHROME;
    return { kind: 'unsupported', name: null };
  }

  const ua = nav?.userAgent ?? '';
  // Order matters: Edge's UA also contains "Chrome/", and Opera's contains both.
  // `Edg/` is Chromium Edge on the desktop. Legacy EdgeHTML ("Edge/") and the
  // mobile builds ("EdgA/", "EdgiOS/") cannot run the extension, so they are not it.
  if (/\bEdg\//.test(ua)) return EDGE;
  if (/\bEdg(e|A|iOS)\//.test(ua)) return { kind: 'unsupported', name: null };
  if (/\bOPR\//.test(ua) || /\bSamsungBrowser\//.test(ua)) return { kind: 'unsupported', name: null };
  if (/\bFirefox\/|\bFxiOS\//.test(ua)) return { kind: 'unsupported', name: 'Firefox' };
  if (/\bChrome\//.test(ua) && !/\bChromium\//.test(ua)) return CHROME;
  if (/\bSafari\//.test(ua) && !/\bChrom(e|ium)\//.test(ua)) return { kind: 'unsupported', name: 'Safari' };
  return { kind: 'unsupported', name: null };
}
